import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { materialService, dynamicFieldService } from "/app/src/services";
import { Search } from "/app/src/components/generic/components/search";
import { useTranslation } from "react-i18next";
import { buildParams } from "/app/src/helpers/params";
import { DynamicField, Material as MaterialType } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * Displays a list of materials with dynamic fields and pagination.
 *
 * @returns {JSX.Element} The rendered Materials component.
 */
export default function Materials() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const goToMaterial = useCallback(
    (materialId: number) => {
      navigate(`/explorer/materials/${materialId}`);
    },
    [navigate],
  );

  const {
    data: materials,
    count: materialsCount,
    isFetching,
  } = usePaginatedData<MaterialType>({
    queryKey: ["materials"],
    searchString,
    sort,
    page,
    pageSize,
    service: materialService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: materialsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const { data: dynamicFields } = useQuery({
    queryKey: ["dynamicFields", "Materialbase"],
    queryFn: () => {
      return dynamicFieldService.getAll(
        buildParams({
          baseTable: "Materialbase",
        }),
      );
    },
    initialData: { dynamic_fields: [] },
    select: (data: { dynamic_fields: DynamicField[] }) => {
      return data.dynamic_fields;
    },
  });

  const columnHelper = createColumnHelper<MaterialType | DynamicField>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      ...dynamicFields.map((dynamicField) =>
        columnHelper.accessor(dynamicField.field, {
          id: dynamicField.field,
          cell: (info) => info.getValue(),
          header: () => dynamicField.name,
        }),
      ),
    ],
    [columnHelper, t, dynamicFields],
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:materials")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={materials}
        tableColumns={columns}
        length={materialsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToMaterial}
        emptyText={t("translation:no_materials")}
      />
    </div>
  );
}
